import { CSSInterpolation } from '@emotion/css/create-instance'
import { css, type SerializedStyles } from '@emotion/react'
import type { BasePlacement, Placement } from '@popperjs/core'
import React, {
  type ComponentProps,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'

import { noop } from '../../util/noop'
import { CssVars, darkTheme } from '../themes'
import { AnchoredOverlay } from './AnchoredOverlay'
import { Markdown } from './Markdown'
import { ThemeProvider } from './ThemeProvider'
import { Text } from './Type'
import { useUniqueId } from './useUniqueId'

type ArrowPosition = 'start' | 'center' | 'end'

type TooltipTriggerProps = {
  /**
   * Content to be used in tooltip. Supports markdown syntax if `variant` prop is set to `markdown`.
   */
  content: string
  /**
   * Render prop for trigger element, this will receive mouse events that should be attached to
   * some type of control such as `Chip`, `Button`, `InfoTag`, or `IconButton` or some other element that is
   * interactable.
   *
   * The function takes `aria-describedby` to help screenreader function properly,
   * `openTooltip` and `closeTooltip` should be attached to relevant event handlers on the control
   */
  renderTrigger: (_: {
    'aria-describedby': string
    closeTooltip: () => void
    openTooltip: () => void
  }) => ReactElement
  /**
   * Allows for alternate positioning of arrow if `hasArrow` is enabled. Only has effect on `top`/`bottom` placements; `left`/`right` remain centered regardless.
   */
  arrowPosition?: ArrowPosition
  /**
   * Z-index of the tooltip. Use sparingly as this may be deprecated in the future.
   */
  dangerouslyEnforceZIndex?: string | null
  /**
   * Delay in ms after hovering into trigger element to show tooltip, defaults to 200
   */
  enterDelay?: number
  /**
   * Gap size between the tooltip content and the trigger element, defaults to 6px
   */
  gap?: number
  /**
   * Whether to show the triangle arrow for tooltip, defaults to false
   */
  hasArrow?: boolean
  /**
   * Optional prop to override default hover behaviors, this changes tooltip to be a controlled component
   */
  isOpen?: boolean
  /**
   * Delay in ms after hovering out of trigger element to hide tooltip, defaults to 200
   */
  leaveDelay?: number
  /**
   * Maximum width allowed for the tooltip content, defaults to 200px
   */
  maxWidth?: number
  /**
   * Optional prop to not hide tooltip when hovered onto, note that this will not work if leaveDelay is 0 or very low
   */
  persistOnContentHover?: boolean
  /**
   * Position of the tooltip element with respect to the trigger element
   */
  placement?: BasePlacement
  /**
   * CSS object if you need to override styles on the div that wraps the element from renderTrigger. Please do this sparingly.
   */
  overrideTriggerContainerStyles?: CSSInterpolation
} & (
  | {
      /**
       * `content` will be rendered as plaintext by default, unless set to `markdown`.
       */
      variant?: 'default'
    }
  | ({
      variant: 'markdown'
    } & Omit<ComponentProps<typeof Markdown>, 'source'>)
)

/**
 * CSS for arrow
 */
// Note (jonathan.wang1) Arrow should be 8px high 12px wide for top/bottom, vice versa for left/right as per Figma.
const arrowSize = 8
const arrowWidth = 6
const baseArrowCss = css({
  borderColor: CssVars.neutral.bg2,
  borderStyle: 'solid',
  borderWidth: arrowSize,
  height: 0,
  position: 'absolute',
  width: 0,
})
const horizontalArrowCss = css({
  borderLeftColor: 'transparent',
  borderLeftWidth: arrowWidth,
  borderRightColor: 'transparent',
  borderRightWidth: arrowWidth,
})
const horizontalArrowCenterCss = css({
  left: '50%',
  transform: 'translateX(-50%)',
})
const horizontalArrowStartCss = css({
  left: 20, // Note (jonathan.wang1) 12 (arrow size) + 8 (Figma design).
  transform: 'translateX(-100%)',
})
const horizontalArrowEndCss = css({
  right: 20,
  transform: 'translateX(100%)',
})

const verticalArrowCss = css({
  borderBottomColor: 'transparent',
  borderBottomWidth: arrowWidth,
  borderTopColor: 'transparent',
  borderTopWidth: arrowWidth,
})
const verticalArrowCenterCss = css({
  top: '50%',
  transform: 'translateY(-50%)',
})

// Note (jonathan.wang1) Currently only bottom/top have custom arrow positioning; left/right always centered.
const handleArrowPosition = (
  placement: BasePlacement,
  arrowPosition: ArrowPosition
) => {
  if (placement === 'bottom' || placement === 'top') {
    if (arrowPosition === 'start') {
      return horizontalArrowStartCss
    }
    if (arrowPosition === 'end') {
      return horizontalArrowEndCss
    }
    return horizontalArrowCenterCss
  }
  return verticalArrowCenterCss
}

const placementToArrowCss: {
  [k in BasePlacement]: Array<SerializedStyles | CSSInterpolation>
} = {
  // top arrow
  bottom: [
    baseArrowCss,
    horizontalArrowCss,
    {
      borderTop: 'none',
      top: -arrowSize,
    },
  ],
  // right arrow
  left: [
    baseArrowCss,
    verticalArrowCss,
    {
      borderRight: 'none',
      right: -arrowSize,
    },
  ],
  // left arrow
  right: [
    baseArrowCss,
    verticalArrowCss,
    {
      borderLeft: 'none',
      left: -arrowSize,
    },
  ],
  // bottom arrow
  top: [
    baseArrowCss,
    horizontalArrowCss,
    {
      borderBottom: 'none',
      bottom: -arrowSize,
    },
  ],
}

function getArrowCssByPlacement(
  placement: Placement,
  arrowPosition: ArrowPosition
): CSSInterpolation | undefined {
  if (['top', 'right', 'bottom', 'left'].includes(placement)) {
    return css([
      ...placementToArrowCss[placement as BasePlacement],
      handleArrowPosition(placement as BasePlacement, arrowPosition),
    ])
  }
}

/**
 * Tooltip trigger component to show layered content in a direction when its children are hovered
 */
export function TooltipTrigger({
  arrowPosition = 'center',
  content,
  dangerouslyEnforceZIndex,
  enterDelay = 200,
  gap = 6,
  hasArrow = false,
  isOpen,
  leaveDelay = 200,
  maxWidth = 200,
  overrideTriggerContainerStyles,
  persistOnContentHover = false,
  placement = 'bottom',
  renderTrigger,
  variant = 'default',
  ...markdownProps
}: TooltipTriggerProps) {
  const isControlledComponent = isOpen !== undefined
  // Note (auster.chen) We don't set the initial state to isOpen for controlled due to AnchoredOverlay not being able to correctly
  // compute position until after components are mounted, alternatively we can render target first then
  // wrap with AnchoredOverlay, since the initial state doesn't matter that much (useEffect updates after first render)
  // this is okay
  const [isVisible, setIsVisible] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const uniqueIdRef = useRef(useUniqueId())

  const openTooltip = () => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current)
    }
    hideTimerRef.current = null

    showTimerRef.current = setTimeout(() => {
      setIsVisible(true)
    }, enterDelay)
  }

  const closeTooltip = () => {
    if (showTimerRef.current !== null) {
      clearTimeout(showTimerRef.current)
    }
    showTimerRef.current = null

    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false)
    }, leaveDelay)
  }

  const onMouseEnterTooltip = () => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current)
    }
  }

  // For controlled usage, we need to continually update isVisibleStatus based on isOpen prop change
  useEffect(() => {
    // can't re-use `isControlledComponent` due to TS issues
    if (isOpen !== undefined) {
      setIsVisible(isOpen)
    }
    if (isOpen && hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
    if (!isOpen && showTimerRef.current !== null) {
      clearTimeout(showTimerRef.current)
      showTimerRef.current = null
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (window !== undefined) {
        if (showTimerRef.current) window.clearTimeout(showTimerRef.current)
        if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
      }
    }
  }, [])

  return (
    <>
      <AnchoredOverlay
        dangerouslyEnforceZIndex={dangerouslyEnforceZIndex}
        gap={hasArrow ? arrowSize + gap : gap}
        isOpen={isVisible}
        placement={placement}
        targetEl={triggerRef.current}
      >
        {({ actualPlacement }) => (
          <ThemeProvider theme={darkTheme}>
            {({ rootClassName }) => {
              const arrowCss =
                hasArrow && actualPlacement
                  ? getArrowCssByPlacement(actualPlacement, arrowPosition)
                  : null

              return (
                <div
                  className={rootClassName}
                  onMouseEnter={
                    persistOnContentHover ? onMouseEnterTooltip : undefined
                  }
                  onMouseLeave={
                    persistOnContentHover ? closeTooltip : undefined
                  }
                >
                  <div
                    css={{
                      background: CssVars.neutral.bg2,
                      borderRadius: 4,
                      boxSizing: 'border-box',
                      maxWidth,
                      padding: 12,
                      position: 'relative',
                      width: ['max-content'],
                    }}
                    id={uniqueIdRef.current}
                    role="tooltip"
                  >
                    <span aria-label="Tooltip: " />
                    {variant === 'markdown' ? (
                      <Markdown source={content} {...markdownProps} />
                    ) : (
                      <Text size="S">{content}</Text>
                    )}
                    {arrowCss != null && <span css={arrowCss} />}
                  </div>
                </div>
              )
            }}
          </ThemeProvider>
        )}
      </AnchoredOverlay>
      <div
        ref={triggerRef}
        css={[{ display: 'inline-block' }, overrideTriggerContainerStyles]}
      >
        {renderTrigger({
          'aria-describedby': uniqueIdRef.current,
          closeTooltip: isControlledComponent ? noop : closeTooltip,
          openTooltip: isControlledComponent ? noop : openTooltip,
        })}
      </div>
    </>
  )
}
